import { lazy, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createHashRouter,
  RouterProvider,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { pageLoaders } from './app/page-loaders';
import { AppLayout } from './app/AppLayout';
import { PreferencesProvider, usePreferences } from './app/PreferencesContext';
import { PwaProvider } from './app/PwaContext';
import { WelcomeDialog } from './app/WelcomeDialog';
import { refreshCloudConfiguration, type CloudBootstrapState } from './cloud/bootstrap';
import { isCloudMode } from './cloud/mode';
import { initializeDatabase } from './data/database';
import { useTeacherData } from './data/hooks';
import { teacherDataService } from './data/repositories';
import { SOIL_LEVELS, TEXTILE_CATEGORIES } from './data/seed';
import { dexieTeacherService } from './features/teacher/dexie-service';
import { cloudTeacherService } from './features/teacher/cloud-service';
import { Alert } from './shared/ui/Alert';
import { Button } from './shared/ui/Button';
import { StateDisplay } from './shared/ui/StateDisplay';

const HomePage = lazy(() => pageLoaders['/']().then((module) => ({ default: module.HomePage })));

const CalculatorPage = lazy(() =>
  pageLoaders['/calculate']().then((module) => ({ default: module.CalculatorPage })),
);
const StainAssistantPage = lazy(() =>
  pageLoaders['/stains']().then((module) => ({ default: module.StainAssistantPage })),
);
const GuidePage = lazy(() =>
  pageLoaders['/guide']().then((module) => ({ default: module.GuidePage })),
);
const TeacherArea = lazy(() =>
  pageLoaders['/teacher']().then((module) => ({ default: module.TeacherArea })),
);

function LoadingPage() {
  return <StateDisplay />;
}

function SchoolRoutes({ cloudState }: { cloudState: CloudBootstrapState }) {
  const { t } = useTranslation();
  const data = useTeacherData();
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, setLocale, theme, setTheme } = usePreferences();
  const hydrated = useRef(false);
  const hydrationTarget = useRef<{ language: typeof locale; theme: typeof theme } | null>(null);

  const settings = data?.settings[0];
  useEffect(() => {
    if (!settings || hydrationTarget.current) return;
    hydrationTarget.current = { language: settings.language, theme: settings.theme };
    setLocale(settings.language);
    setTheme(settings.theme);
  }, [setLocale, setTheme, settings]);

  useEffect(() => {
    if (!settings || !hydrationTarget.current) return;
    if (!hydrated.current) {
      if (locale === hydrationTarget.current.language && theme === hydrationTarget.current.theme)
        hydrated.current = true;
      return;
    }
    if (settings.language === locale && settings.theme === theme) return;
    void (isCloudMode
      ? teacherDataService.updateDevicePreferences({ language: locale, theme })
      : teacherDataService.updateSettings({ language: locale, theme }));
  }, [locale, settings, theme]);

  if (!data || !settings) return <LoadingPage />;

  const calculatorData = {
    settings,
    products: data.products,
    dosageTables: data.dosageTables,
    waterHardness: data.waterHardness,
    containers: data.containers,
    measuringCups: data.measuringCups,
    conversionConfigurations: data.conversionConfigurations,
    recommendationRules: data.recommendationRules,
    temperatureGuidance: data.temperatureGuidance,
    soilLevels: [...SOIL_LEVELS],
    textileCategories: [...TEXTILE_CATEGORIES],
  };

  return (
    <>
      <WelcomeDialog
        open={
          !settings.welcomeDismissed &&
          (location.pathname === '/calculate' || location.pathname === '/stains')
        }
        onDismiss={() =>
          void (isCloudMode
            ? teacherDataService.updateDevicePreferences({ welcomeDismissed: true })
            : teacherDataService.updateSettings({ welcomeDismissed: true }))
        }
      />

      <Routes>
        <Route element={<AppLayout cloudState={cloudState} />}>
          <Route index element={<HomePage schoolName={settings.schoolName} />} />
          <Route path="/calculate" element={<CalculatorPage data={calculatorData} />} />
          <Route
            path="/stains"
            element={
              <StainAssistantPage
                guides={data.stainGuides}
                textileCategories={[...TEXTILE_CATEGORIES]}
                products={data.products}
              />
            }
          />
          <Route
            path="/guide"
            element={
              <GuidePage
                products={data.products}
                temperatureGuidance={data.temperatureGuidance}
                textileCategories={[...TEXTILE_CATEGORIES]}
              />
            }
          />
          <Route
            path="/teacher"
            element={
              <TeacherArea
                service={isCloudMode ? cloudTeacherService : dexieTeacherService}
                locale={locale}
                onClose={() => {
                  void navigate('/calculate', { viewTransition: true });
                }}
                onLocaleChange={setLocale}
                onThemeChange={setTheme}
              />
            }
          />
          <Route
            path="*"
            element={
              <div className="page stack">
                <Alert kind="warning">{t('errors.generic')}</Alert>
                <Button
                  onClick={() => {
                    void navigate('/calculate', { viewTransition: true });
                  }}
                >
                  {t('nav.calculator')}
                </Button>
              </div>
            }
          />
        </Route>
      </Routes>
    </>
  );
}

function DatabaseGate() {
  const { t } = useTranslation();
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'ready'; cloud: CloudBootstrapState } | { status: 'error' }
  >({ status: 'loading' });
  useEffect(() => {
    let active = true;
    initializeDatabase()
      .then(() => refreshCloudConfiguration())
      .then((cloud) => active && setState({ status: 'ready', cloud }))
      .catch(() => active && setState({ status: 'error' }));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isCloudMode || state.status !== 'ready') return;
    let active = true;
    const refresh = () => {
      void refreshCloudConfiguration().then((cloud) => {
        if (active) setState({ status: 'ready', cloud });
      });
    };
    const visibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const offline = () => {
      setState((current) =>
        current.status === 'ready'
          ? {
              status: 'ready',
              cloud: {
                ...current.cloud,
                availability: current.cloud.revision === null ? 'defaults' : 'cached',
              },
            }
          : current,
      );
    };
    window.addEventListener('online', refresh);
    window.addEventListener('offline', offline);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      active = false;
      window.removeEventListener('online', refresh);
      window.removeEventListener('offline', offline);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [state.status]);

  if (state.status === 'loading') return <LoadingPage />;
  if (state.status === 'error') {
    return (
      <main className="error-page">
        <img src="./app-icon.svg" alt="" width="96" height="96" />
        <h1>{t('errors.title')}</h1>
        <p>{t('errors.database')}</p>
        <Button onClick={() => window.location.reload()}>{t('common.retry')}</Button>
      </main>
    );
  }
  return <SchoolRoutes cloudState={state.cloud} />;
}

const router = createHashRouter([
  {
    path: '*',
    loader: async ({ request }) => {
      const path = new URL(request.url).pathname as keyof typeof pageLoaders;
      if (pageLoaders[path]) await pageLoaders[path]();
      return null;
    },
    element: <DatabaseGate />,
    hydrateFallbackElement: <LoadingPage />,
  },
]);

export default function App() {
  return (
    <PreferencesProvider>
      <PwaProvider>
        <RouterProvider router={router} />
      </PwaProvider>
    </PreferencesProvider>
  );
}
